import React, { useEffect, useRef, useState } from 'react';
import { MDBBtn, MDBIcon, MDBInput } from 'mdb-react-ui-kit';
import { useTranslation } from 'react-i18next';
import { generateClient } from 'aws-amplify/api';
import useDebounce from '../../utils/debounce';
import { captureError } from '../../utils/sentryHelpers';
import { isCanonicalSlug, normalizeSlug } from '../../utils/slugify';
import {
  extractSlugLookupItems,
  findSlugOwner,
  publicPathForSlug,
  slugLookupQuery,
} from '../../utils/slugInput';

const client = generateClient();

/**
 * Admin/Moderator input for the public URL slug of an entity.
 *
 * - "generate from name" sets normalizeSlug(sourceText)
 * - the value is normalized on blur
 * - a debounced bySlug query warns when another entity already owns the slug
 * - shows the resulting public path
 *
 * The field may be left empty: the backend then generates the slug from the
 * name. Callers must only render it for Admin/Moderator (see utils/roles.js)
 * and must only include "slug" in their mutation input for those groups.
 *
 * @param {object} props
 * @param {string|null} props.value
 * @param {(slug: string) => void} props.onChange
 * @param {string} [props.sourceText] display name the slug is generated from
 * @param {'company'|'product'|'news'|'lecture'|'job'|'event'|'mediaSeries'|'mediaItem'} props.entity
 * @param {string} [props.entityId] id of the entity being edited (excluded from the uniqueness check)
 * @param {string} [props.label]
 * @param {boolean} [props.disabled]
 * @param {string} [props.helpText]
 * @param {string[]} [props.history] previous slugs (read only, informational)
 * @param {string} [props.scopeValue] for scoped entities (SLUG_ENTITIES[entity].scopeField, e.g. the
 *   mediaSeriesId of a MediaItem): only owners within this scope count as conflicts
 * @param {string} [props.pathPrefixOverride] replaces the entity's public path prefix in the hint
 */
export default function SlugField({ value, onChange, sourceText, entity, entityId, label, disabled, helpText, history, scopeValue, pathPrefixOverride }) {
  const { t } = useTranslation();
  const slug = typeof value === 'string' ? value : '';
  const debouncedSlug = useDebounce(slug, 500);
  const [owner, setOwner] = useState(null);
  const [isChecking, setIsChecking] = useState(false);
  const requestCounter = useRef(0);

  const emit = (next) => {
    if (typeof onChange === 'function') onChange(next);
  };

  // Uniqueness check
  useEffect(() => {
    const query = slugLookupQuery(entity);
    const candidate = debouncedSlug.trim();
    if (!query || !candidate || !isCanonicalSlug(candidate)) {
      setOwner(null);
      setIsChecking(false);
      return undefined;
    }
    const requestId = ++requestCounter.current;
    let cancelled = false;
    const isCurrent = () => !cancelled && requestId === requestCounter.current;
    setIsChecking(true);
    // Scoped slugs (e.g. MediaItem per series) may exist in several scopes: fetch more candidates
    const limit = scopeValue ? 50 : 5;
    client.graphql({ query, variables: { slug: candidate, limit } })
      .then((response) => {
        if (!isCurrent()) return;
        setOwner(findSlugOwner(extractSlugLookupItems(response, entity), entityId, entity, scopeValue));
      })
      .catch((error) => {
        if (!isCurrent()) return;
        console.error('Slug availability check failed:', error);
        captureError(error, {
          feature: 'slug',
          operation: 'slug-availability-check',
          context: { entity, slug: candidate, scoped: Boolean(scopeValue) },
          tags: { component: 'SlugField' },
        });
        // A rejected GraphQLResult may still carry partial data; otherwise hide the hint.
        const items = extractSlugLookupItems(error, entity);
        setOwner(items ? findSlugOwner(items, entityId, entity, scopeValue) : null);
      })
      .finally(() => {
        if (isCurrent()) setIsChecking(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSlug, entity, entityId, scopeValue]);

  const handleBlur = () => {
    const normalized = normalizeSlug(slug);
    if (normalized !== slug) emit(normalized);
  };

  const handleGenerate = () => {
    emit(normalizeSlug(sourceText || ''));
  };

  const normalizedPreview = normalizeSlug(slug);
  const needsNormalization = slug.length > 0 && !isCanonicalSlug(slug);
  const publicPath = publicPathForSlug(entity, normalizedPreview, pathPrefixOverride);

  return (
    <div className="mb-3">
      <div className="d-flex align-items-start gap-2">
        <div className="flex-grow-1">
          <MDBInput
            label={label || t("URL slug")}
            type="text"
            autoComplete="off"
            value={slug}
            disabled={disabled}
            className={owner ? "is-invalid" : ""}
            onChange={(e) => emit(e.target.value)}
            onBlur={handleBlur}
          />
        </div>
        <MDBBtn
          type="button"
          color="secondary"
          outline
          onClick={handleGenerate}
          disabled={disabled || !sourceText}
          title={t("Generate from name")}
        >
          <MDBIcon fas icon="magic" />
        </MDBBtn>
      </div>
      {owner && (
        <div className="text-danger small mt-1">
          {t("This slug is already used by {{name}}", { name: owner.name })}
        </div>
      )}
      {isChecking && !owner && (
        <div className="text-muted small mt-1">{t("Checking availability...")}</div>
      )}
      {needsNormalization && (
        <div className="text-muted small mt-1">
          {t("Will be saved as")}: <code>{normalizedPreview || "-"}</code>
        </div>
      )}
      {publicPath && (
        <div className="text-muted small mt-1">
          {t("Public URL")}: <code>{publicPath}</code>
        </div>
      )}
      {!slug && (
        <div className="text-muted small mt-1">
          {helpText || t("Leave empty to generate the slug automatically from the name.")}
        </div>
      )}
      {Array.isArray(history) && history.length > 0 && (
        <div className="text-muted small mt-1">
          {t("Previous slugs")}: {history.join(", ")}
        </div>
      )}
    </div>
  );
}
